/**
 * Everflow affiliate params (offer, offer URL, affiliate, sub1-8) as captured
 * from a landing URL. Field names deliberately match
 * `EnrollmentItemTrackingMetadata` so values flow into
 * `enrollmentItem.metadata.tracking` without remapping.
 *
 * Unlike the Everflow transaction ID (visitor identity — cookie-only, never
 * URL; see getEverflowTransactionIdFromCookies), these are campaign
 * identifiers that legitimately arrive via URL query params (`oid`, `uid`,
 * `affid`, `sub1`-`sub8`) — the same params the form funnel and the sitewide
 * EverflowSDK.click() call already read.
 *
 * They are persisted to a first-party cookie on landing (see EverflowScript)
 * because the query string is gone after the first navigation, but leads are
 * usually created pages later (marketplace intake, program flow, clickout).
 */
export interface EverflowAffiliateParams {
  everflowOfferId?: string;
  /** Everflow offer URL id (`network_offer_url_id`, the `uid` param) — which
   *  destination URL of the offer this click used. Distinct from
   *  `everflowOfferId`, which is our single offer (always "1"). Facebook
   *  campaigns are set up one offer URL per campaign, so this identifies the
   *  campaign when sub1-8 are missing. */
  everflowOfferUrlId?: string;
  everflowAffiliateId?: string;
  everflowSub1?: string;
  everflowSub2?: string;
  everflowSub3?: string;
  everflowSub4?: string;
  everflowSub5?: string;
  everflowSub6?: string;
  everflowSub7?: string;
  everflowSub8?: string;
}

const URL_PARAM_TO_FIELD = {
  oid: "everflowOfferId",
  uid: "everflowOfferUrlId",
  affid: "everflowAffiliateId",
  sub1: "everflowSub1",
  sub2: "everflowSub2",
  sub3: "everflowSub3",
  sub4: "everflowSub4",
  sub5: "everflowSub5",
  sub6: "everflowSub6",
  sub7: "everflowSub7",
  sub8: "everflowSub8",
} as const satisfies Record<string, keyof EverflowAffiliateParams>;

/**
 * Every affiliate-param field a landing can populate. The server-side
 * click-attribution key list (buildEverflowTracking) composes from this so
 * the two lists can't drift when a sub param is added.
 */
export const AFFILIATE_PARAM_FIELDS = Object.values(URL_PARAM_TO_FIELD);

/**
 * The subset of AFFILIATE_PARAM_FIELDS that proves a landing carried real
 * Everflow *attribution* — the values we would otherwise back-fill from the
 * click log. Used to answer "did this capture already have attribution, or
 * should we go fetch it?".
 *
 * Deliberately EXCLUDES `everflowOfferUrlId`: a landing can carry `uid` alone
 * (it selects which offer URL was used) with no affiliate id and no subs.
 * Treating that as "already attributed" would suppress the click-log fill that
 * is the only thing populating the sub params for that lead — the exact case
 * this project exists to fix.
 */
export const AFFILIATE_ATTRIBUTION_FIELDS = AFFILIATE_PARAM_FIELDS.filter(
  (field) => field !== "everflowOfferUrlId"
);

/**
 * Parses Everflow affiliate params from a URL query string (`?oid=..&sub1=..`).
 * Returns null when no Everflow param is present so callers don't overwrite a
 * previously captured cookie with an empty object (last-touch: a new landing
 * WITH params replaces the whole set; a landing without params leaves the
 * previous capture intact).
 */
export function parseEverflowAffiliateParamsFromSearch(
  search: string
): EverflowAffiliateParams | null {
  const urlParams = new URLSearchParams(search);
  const params: EverflowAffiliateParams = {};
  for (const [urlKey, field] of Object.entries(URL_PARAM_TO_FIELD)) {
    const value = urlParams.get(urlKey);
    if (value) params[field] = value;
  }
  return Object.keys(params).length > 0 ? params : null;
}

/**
 * Parses the affiliate-params cookie value (JSON written by EverflowScript).
 * Tolerates both URI-encoded (js-cookie's default write encoding) and raw
 * JSON, and drops unknown keys / non-string values so a tampered cookie can
 * never inject unexpected shapes downstream. Returns null on any parse
 * failure or when nothing valid remains.
 */
export function parseEverflowAffiliateParamsCookieValue(
  raw: string | undefined
): EverflowAffiliateParams | null {
  if (!raw) return null;
  for (const candidate of [raw, safeDecodeURIComponent(raw)]) {
    if (!candidate) continue;
    try {
      const parsed: unknown = JSON.parse(candidate);
      if (typeof parsed !== "object" || parsed === null) return null;
      const params: EverflowAffiliateParams = {};
      for (const field of AFFILIATE_PARAM_FIELDS) {
        const value = (parsed as Record<string, unknown>)[field];
        if (typeof value === "string" && value) params[field] = value;
      }
      return Object.keys(params).length > 0 ? params : null;
    } catch {
      // Fall through to the next decoding candidate.
    }
  }
  return null;
}

function safeDecodeURIComponent(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}
